import { DateOnlyInput } from '../components/DateOnlyInput';
import './alpha-finance-tables.css';
import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { ErrorState } from '../ui/State';
import { PageHeader } from '../ui/PageHeader';
export function YearEndBalancingAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building') ?? '',
    root = '/api/alpha/year-end-balancing/' + building;
  const [data, setData] = useState<any>(),
    [selected, setSelected] = useState(''),
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const load = () => api(root).then(setData),
    detail = (id: string) => {
      setSelected(id);
      return api(root + '/' + id).then(setModel);
    };
  useEffect(() => {
    if (building) void load().catch(setError);
  }, [building]);
  async function save(path: string, body: object) {
    setBusy(true);
    try {
      const result = await api<{ id: string }>(path, {
        method: 'POST',
        body: JSON.stringify({ ...body, idempotencyKey: crypto.randomUUID() }),
      });
      await load();
      await detail(selected || result.id);
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function prepare(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void save(root, {
      reviewPackId: f.get('pack'),
      targetPeriodId: f.get('period'),
      effectiveDate: f.get('date'),
      dueDate: f.get('due'),
      evidenceVersionId: f.get('evidence'),
      reason: f.get('reason'),
    });
  }
  return (
    <>
      <PageHeader title="Year-end balancing"
        context="Preview the approved operating result against recorded percentage shares. Review the lease basis and liable owners before submitting. Each non-zero amount then requires separate approval through Income control; no payment is taken or refunded here."
        secondaryActions={building?[{kind:'link',label:'Open income control for approval and posting',to:'/finance/service-charges?building='+building}]:[]}/>
      {Boolean(error) && <ErrorState error={error} />}
      {data && (
        <>
          {!data.proposals.length&&<p className="empty">No balancing proposals have been prepared for this building.</p>}
          <nav aria-label="Balancing proposals">
            {data.proposals.map((p: any) => (
              <p key={p.id}>
                <button onClick={() => void detail(p.id).catch(setError)}>
                  £{p.total} · {p.effective_date} ·{' '}
                  {p.withdrawn_at
                    ? 'Withdrawn'
                    : p.issued_at
                      ? 'Submitted for approval'
                      : 'Preview'}
                </button>
              </p>
            ))}
          </nav>
          <details>
            <summary>Prepare balancing preview</summary>
            <form className="form" onSubmit={prepare}>
              <fieldset disabled={busy}>
                <label>
                  Approved year-end pack
                  <select name="pack" required>
                    <option value="">Choose pack</option>
                    {data.packs.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.title} · Version {p.version}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Posting period
                  <select name="period" required>
                    <option value="">Choose subsequent open period</option>
                    {data.periods.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Posting date
                  <DateOnlyInput name="date" required />
                </label>
                <label>
                  Due date
                  <DateOnlyInput name="due" required />
                </label>
                <label>
                  Reviewed supporting evidence
                  <select name="evidence" required>
                    <option value="">Choose evidence</option>
                    {data.documents.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Lease basis and liability review
                  <textarea name="reason" minLength={10} maxLength={2000} required />
                </label>
                <button>Prepare preview</button>
              </fieldset>
            </form>
          </details>
        </>
      )}
      {model && (
        <section className="panel">
          <h2>Allocation preview · £{model.proposal.total}</h2>
          <p>
            Positive amounts are debit adjustments; negative amounts are credits. Rounding uses the
            largest remainder, tied by account, and reconciles exactly to the reviewed total.
          </p>
          <table className="alpha-finance-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Liable owner</th>
                <th>Share</th>
                <th>Amount</th>
                <th>Approval</th>
              </tr>
            </thead>
            <tbody>
              {model.proposal.snapshot.map((l: any) => (
                <tr key={l.accountId}>
                  <td>{l.display_name}</td>
                  <td>{l.owner}</td>
                  <td>{l.share_percent}%</td>
                  <td>£{l.amount}</td>
                  <td>
                    {model.lines.find((a: any) => a.account_id === l.accountId)?.status ??
                      'Not submitted'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>{model.proposal.reason}</p>
          {!model.proposal.issued_at && !model.proposal.withdrawn_at && (
            <button disabled={busy} onClick={() => void save(root + '/' + selected + '/issue', {})}>
              Submit reviewed allocations for approval
            </button>
          )}
          {!model.proposal.issued_at && !model.proposal.withdrawn_at && (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                void save(root + '/' + selected + '/withdraw', {
                  reason: new FormData(e.currentTarget).get('reason'),
                });
              }}
            >
              <label>
                Reason for replacing this preview
                <input name="reason" required minLength={5} maxLength={2000} />
              </label>
              <button disabled={busy}>Withdraw preview for replacement</button>
            </form>
          )}
        </section>
      )}
    </>
  );
}
